"use client";
import { TimelineContent } from "@/components/ui/timeline-animation";
import { VerticalCutReveal } from "@/components/ui/vertical-cut-reveal";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Facebook, Linkedin, ArrowRight, Phone, Mail, MapPin, Clock, Calendar, MessageCircle, Send, CheckCircle, Shield, Award, Users } from "lucide-react";
import { useRef, useState, useEffect } from "react";
import { track } from "@/lib/analytics";
import { useToast } from "@/hooks/use-toast";
import { sendContactEmail } from "@/lib/supabase";
import heroSolar from "@/assets/hero-solar.jpg";

export default function ContactSection() {
  const heroRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  
  const [contactForm, setContactForm] = useState({
    naam: "",
    email: "",
    telefoon: "",
    onderwerp: "",
    bericht: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup script on unmount
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);
  
  const revealVariants = {
    visible: (i: number) => ({
      y: 0,
      opacity: 1,
      filter: "blur(0px)",
      transition: {
        delay: i * 0.3,
        duration: 0.6,
        ease: "easeOut"
      },
    }),
    hidden: {
      filter: "blur(10px)",
      y: -20,
      opacity: 0,
    },
  };
  
  const scaleVariants = {
    visible: (i: number) => ({
      opacity: 1,
      filter: "blur(0px)",
      scale: 1,
      transition: {
        delay: i * 0.3,
        duration: 0.6,
        ease: "easeOut"
      },
    }),
    hidden: {
      filter: "blur(10px)",
      opacity: 0,
      scale: 0.95,
    },
  };

  const handleSocialClick = (platform: string, url: string) => {
    track("contact", { method: platform });
    window.open(url, "_blank", "noopener noreferrer");
  };

  const handleInputChange = (field: keyof typeof contactForm, value: string) => {
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsSubmitting(true);
    
    try {
      const result = await sendContactEmail(contactForm);
      if (result.success) {
        toast({
          title: "Bericht verzonden!",
          description: "We nemen binnen 2 uur contact met u op."
        });
        setContactForm({
          naam: "",
          email: "",
          telefoon: "",
          onderwerp: "",
          bericht: ""
        });
      } else {
        toast({
          title: "Fout bij verzenden",
          description: "Er is een fout opgetreden. Probeer het later opnieuw of bel ons direct.",
          variant: "destructive"
        });
      }
    } catch (error) {
      toast({
        title: "Fout bij verzenden",
        description: "Er is een fout opgetreden. Probeer het later opnieuw of bel ons direct.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCalendlyClick = () => {
    track("generate_lead", { source: "contact_page_calendly" });
    const calendlySection = document.getElementById('calendly-section');
    calendlySection?.scrollIntoView({ behavior: 'smooth' });
  };

  const contactMethods = [
    {
      icon: Phone,
      title: "Direct Bellen",
      description: "Krijg direct persoonlijk advies",
      action: "06 17 10 57 86",
      link: "tel:0617105786",
      color: "primary",
      urgent: true
    },
    {
      icon: MessageCircle,
      title: "WhatsApp",
      description: "Snelle reactie via WhatsApp",
      action: "Start Chat",
      link: "https://wa.me/31617105786",
      color: "secondary"
    },
    {
      icon: Mail,
      title: "E-mail",
      description: "Uitgebreide vragen per e-mail",
      action: "info@sol-vi.nl",
      link: "mailto:info@sol-vi.nl",
      color: "accent"
    },
    {
      icon: Calendar,
      title: "Adviesgesprek",
      description: "Plan een gratis locatiebezoek",
      action: "Plan Afspraak",
      link: "#calendly",
      color: "primary"
    }
  ];

  return (
    <section className="py-8 px-4 bg-muted/30" ref={heroRef}>
      <div className="max-w-6xl mx-auto">
        <div className="relative">
          {/* Header with social icons */}
          <div className="flex justify-between items-center mb-8 w-[85%] absolute lg:top-4 md:top-0 sm:-top-2 -top-3 z-10">
            <div className="flex items-center gap-2 text-xl animate-fade-in">
              <span className="text-primary animate-spin text-lg">✱</span>
              <span className="text-sm font-medium text-muted-foreground">
                CONTACT
              </span>
            </div>
            <div className="flex gap-4">
              <button
                onClick={() => handleSocialClick("facebook", "https://www.facebook.com/profile.php?id=61565954054747")}
                className="md:w-8 md:h-8 sm:w-6 w-5 sm:h-6 h-5 border border-border bg-card rounded-lg flex items-center justify-center hover:bg-accent transition-colors cursor-pointer hover-scale"
              >
                <Facebook className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleSocialClick("linkedin", "https://www.linkedin.com/company/sol-vi/")}
                className="md:w-8 md:h-8 sm:w-6 w-5 sm:h-6 h-5 border border-border bg-card rounded-lg flex items-center justify-center hover:bg-accent transition-colors cursor-pointer hover-scale"
              >
                <Linkedin className="h-4 w-4" />
              </button>
            </div>
          </div>

          <figure className="relative group animate-scale-in">
            <svg
              className="w-full"
              width="100%"
              height="100%"
              viewBox="0 0 100 40"
              xmlns="http://www.w3.org/2000/svg"
            >
              <defs>
                <clipPath
                  id="clip-inverted"
                  clipPathUnits="objectBoundingBox"
                >
                  <path
                    d="M0.0998072 1H0.422076H0.749756C0.767072 1 0.774207 0.961783 0.77561 0.942675V0.807325C0.777053 0.743631 0.791844 0.731953 0.799059 0.734076H0.969813C0.996268 0.730255 1.00088 0.693206 0.999875 0.675159V0.0700637C0.999875 0.0254777 0.985045 0.00477707 0.977629 0H0.902473C0.854975 0 0.890448 0.138535 0.850165 0.138535H0.0204424C0.00408849 0.142357 0 0.180467 0 0.199045V0.410828C0 0.449045 0.0136283 0.46603 0.0204424 0.469745H0.0523086C0.0696245 0.471019 0.0735527 0.497877 0.0733523 0.511146V0.915605C0.0723903 0.983121 0.090588 1 0.0998072 1Z"
                    fill="#D9D9D9"
                  />
                </clipPath>
              </defs>
              <image
                clipPath="url(#clip-inverted)"
                preserveAspectRatio="xMidYMid slice"
                width="100%"
                height="100%"
                href={heroSolar}
              />
            </svg>
          </figure>

          {/* Stats */}
          <div className="flex flex-wrap lg:justify-start justify-between items-center py-3 text-sm">
            <div className="flex gap-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="flex items-center gap-2 mb-2 sm:text-base text-xs">
                <span className="text-primary font-bold">2u</span>
                <span className="text-muted-foreground">reactietijd</span>
                <span className="text-border">|</span>
              </div>
              <div className="flex items-center gap-2 mb-2 sm:text-base text-xs">
                <span className="text-primary font-bold">24/7</span>
                <span className="text-muted-foreground">bereikbaar</span>
              </div>
            </div>
            <div className="lg:absolute right-0 bottom-16 flex lg:flex-col flex-row-reverse lg:gap-0 gap-4">
              <div className="flex lg:text-4xl sm:text-3xl text-2xl items-center gap-2 mb-2 animate-fade-in" style={{ animationDelay: '0.6s' }}>
                <span className="text-primary font-semibold">Gratis</span>
                <span className="text-muted-foreground uppercase">Advies</span>
              </div>
              <div className="flex items-center gap-2 mb-2 sm:text-base text-xs animate-fade-in" style={{ animationDelay: '0.9s' }}>
                <span className="text-primary font-bold">0</span>
                <span className="text-muted-foreground">verplichtingen</span>
                <span className="text-border lg:hidden block">|</span>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <h1 className="sm:text-4xl md:text-5xl text-2xl !leading-[110%] font-semibold text-foreground mb-8">
              <VerticalCutReveal
                splitBy="words"
                staggerDuration={0.1}
                staggerFrom="first"
                reverse={true}
                transition={{
                  type: "spring",
                  stiffness: 250,
                  damping: 30,
                  delay: 3,
                }}
              >
                Direct contact met duurzame energie experts
              </VerticalCutReveal>
            </h1>

            {/* Contact Methods */}
            <div className="grid sm:grid-cols-2 gap-4 mb-8 animate-fade-in" style={{ animationDelay: '1.2s' }}>
              {contactMethods.map((method, index) => {
                const Icon = method.icon;
                const isCalendly = method.link === "#calendly";
                
                return (
                  <div
                    key={index}
                    className={`group relative overflow-hidden transition-all duration-300 hover:scale-105 p-4 bg-card rounded-lg border-2 hover:border-${method.color}/30 animate-fade-in ${
                      method.urgent ? 'ring-2 ring-primary/20 bg-gradient-to-br from-primary/5 to-transparent' : ''
                    }`}
                    style={{ animationDelay: `${1.5 + index * 0.2}s` }}
                  >
                    {method.urgent && (
                      <div className="absolute top-2 right-2 bg-primary text-primary-foreground text-xs px-2 py-1 rounded-full font-semibold">
                        MEEST DIRECT
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full bg-gradient-to-br from-${method.color}/20 to-${method.color}/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className={`h-6 w-6 text-${method.color}`} />
                      </div>
                      
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm">{method.title}</h3>
                        <p className="text-muted-foreground text-xs mb-2">{method.description}</p>
                        
                        <Button
                          variant={method.urgent ? "hero" : "outline"}
                          size="sm"
                          className="group-hover:shadow-lg transition-all duration-300"
                          onClick={isCalendly ? handleCalendlyClick : undefined}
                          asChild={!isCalendly}
                        >
                          {isCalendly ? (
                            <>
                              <Calendar className="mr-2 h-3 w-3" />
                              {method.action}
                            </>
                          ) : (
                            <a href={method.link} target={method.link.startsWith('http') ? '_blank' : undefined} rel={method.link.startsWith('http') ? 'noopener noreferrer' : undefined}>
                              <Icon className="mr-2 h-3 w-3" />
                              {method.action}
                            </a>
                          )}
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Contact Form */}
            <div className="bg-card p-6 rounded-lg border mb-8">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Stuur een Bericht</h3>
              <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="naam">Naam *</Label>
                    <Input
                      id="naam"
                      name="naam"
                      type="text"
                      value={contactForm.naam}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleInputChange('naam', e.target.value);
                      }}
                      required
                      autoComplete="name"
                      className="transition-all duration-300 focus:scale-[1.02]"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="telefoon">Telefoon</Label>
                    <Input
                      id="telefoon"
                      name="telefoon"
                      type="tel"
                      value={contactForm.telefoon}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleInputChange('telefoon', e.target.value);
                      }}
                      autoComplete="tel"
                      className="transition-all duration-300 focus:scale-[1.02]"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email">E-mail *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={contactForm.email}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleInputChange('email', e.target.value);
                    }}
                    required
                    autoComplete="email"
                    className="transition-all duration-300 focus:scale-[1.02]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="onderwerp">Onderwerp *</Label>
                  <Input
                    id="onderwerp"
                    name="onderwerp"
                    type="text"
                    value={contactForm.onderwerp}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleInputChange('onderwerp', e.target.value);
                    }}
                    required
                    placeholder="Bijv: Advies zonnepanelen, offerte warmtepomp..."
                    className="transition-all duration-300 focus:scale-[1.02]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="bericht">Bericht *</Label>
                  <Textarea
                    id="bericht"
                    name="bericht"
                    value={contactForm.bericht}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleInputChange('bericht', e.target.value);
                    }}
                    required
                    rows={4}
                    placeholder="Vertel ons over uw situatie, wensen en vragen..."
                    className="transition-all duration-300 focus:scale-[1.02] resize-none"
                  />
                </div>
                
                <Button 
                  type="submit" 
                  variant="hero" 
                  className="w-full transition-all duration-300 hover:scale-[1.02]"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                      Versturen...
                    </div>
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      Verstuur Bericht
                    </>
                  )}
                </Button>
              </form>
            </div>

            {/* Trust Signals */}
            <div className="grid sm:grid-cols-3 gap-4 mb-8 animate-fade-in" style={{ animationDelay: '2.3s' }}>
              <div className="text-center p-4 bg-card rounded-lg border">
                <CheckCircle className="h-8 w-8 text-primary mx-auto mb-2" />
                <h4 className="font-medium text-foreground">2u Reactietijd</h4>
                <p className="text-sm text-muted-foreground">Gegarandeerde snelle respons</p>
              </div>
              <div className="text-center p-4 bg-card rounded-lg border">
                <Shield className="h-8 w-8 text-primary mx-auto mb-2" />
                <h4 className="font-medium text-foreground">Gratis Advies</h4>
                <p className="text-sm text-muted-foreground">Geen kosten, wel kwaliteit</p>
              </div>
              <div className="text-center p-4 bg-card rounded-lg border">
                <Award className="h-8 w-8 text-primary mx-auto mb-2" />
                <h4 className="font-medium text-foreground">Geen Verplichtingen</h4>
                <p className="text-sm text-muted-foreground">Vrijblijvend adviesgesprek</p>
              </div>
            </div>
          </div>

          <div className="md:col-span-1">
            <div className="text-right">
              <div className="text-primary text-2xl font-bold mb-2 animate-fade-in" style={{ animationDelay: '2.6s' }}>
                SOL-VI
              </div>
              <div className="text-muted-foreground text-sm mb-4 animate-fade-in" style={{ animationDelay: '2.9s' }}>
                Specialist in duurzame energie
              </div>
              
              <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground mb-8 animate-fade-in" style={{ animationDelay: '3.2s' }}>
                <MapPin className="h-4 w-4" />
                <span>Elst, Gelderland</span>
              </div>

              {/* Business Info */}
              <div className="bg-card p-4 rounded-lg border mb-6 animate-fade-in" style={{ animationDelay: '3.5s' }}>
                <h4 className="font-semibold mb-3 text-foreground">Contactgegevens</h4>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-primary" />
                    <span>Prinswillemalexander straat<br />6661 XH Elst</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-primary" />
                    <span>06 17 10 57 86</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-primary" />
                    <span>info@sol-vi.nl</span>
                  </div>
                </div>
              </div>

              <div className="bg-card p-4 rounded-lg border mb-6 animate-fade-in" style={{ animationDelay: '3.8s' }}>
                <h4 className="font-semibold mb-3 text-foreground">Openingstijden</h4>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p><span className="font-medium">Ma - Vr:</span> 08:00 - 18:00</p>
                  <p><span className="font-medium">Zaterdag:</span> 09:00 - 16:00</p>
                  <p><span className="font-medium">Zondag:</span> Gesloten</p>
                  <p className="text-primary text-xs mt-2">
                    <strong>Spoed? Bel altijd!</strong>
                  </p>
                </div>
              </div>

              <div className="space-y-3 animate-fade-in" style={{ animationDelay: '4.1s' }}>
                <Button
                  onClick={() => window.open('tel:0617105786', '_self')}
                  className="w-full bg-neutral-900 hover:bg-neutral-950 shadow-lg shadow-neutral-900/25 border border-neutral-700 flex gap-2 hover:gap-4 transition-all duration-300 ease-in-out text-white font-semibold"
                >
                  BEL DIRECT
                  <ArrowRight className="h-4 w-4" />
                </Button>
                <Button
                  onClick={handleCalendlyClick}
                  variant="outline"
                  className="w-full"
                >
                  PLAN AFSPRAAK
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Calendly Section */}
        <div id="calendly-section" className="mt-12">
          <div className="bg-card p-6 rounded-lg border animate-fade-in" style={{ animationDelay: '4.4s' }}>
            <h3 className="text-xl font-semibold mb-4 text-foreground text-center">Plan Direct een Afspraak</h3>
            <p className="text-muted-foreground mb-6 text-center">
              Kies een moment dat u uitkomt voor een gratis adviesgesprek bij u thuis.
            </p>
            <div 
              className="calendly-inline-widget rounded-lg overflow-hidden" 
              data-url="https://calendly.com/sol-vi/new-meeting?primary_color=ff9429" 
              style={{
                minWidth: '100%',
                height: '700px'
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
}